// src/hooks/useSecureApi.js - Hooks seguros para React con APISix

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import secureApi from '../services/secureApiService';
import { useAuth } from '../context/AuthContext';

// Hook principal para autenticación segura - Ahora consume el AuthContext
export const useSecureAuth = () => {
  return useAuth();
};

// Hook para datos seguros con cache y revalidación
export const useSecureData = (endpoint, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const {
    dependencies = [],
    cacheTTL = 5 * 60 * 1000, // 5 minutos por defecto
    retryCount = 3,
    retryDelay = 1000,
    revalidateOnFocus = true,
  } = options;

  const abortControllerRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  // Función de fetch con retry y exponential backoff
  const fetchWithRetry = useCallback(async (attempt = 1) => {
    try {
      // Cancelar request anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setError(null);
      if (attempt === 1) setLoading(true);

      const result = await secureApi.secureRequest(endpoint, {
        ...options.requestOptions,
      });

      setData(result);
      setLastFetch(new Date());

    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Request cancelado, no es error
      }

      console.error(`Error en fetch (intento ${attempt}):`, error.message);

      if (attempt < retryCount) {
        const delay = retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
        retryTimeoutRef.current = setTimeout(() => {
          fetchWithRetry(attempt + 1);
        }, delay);
      } else {
        setError(error.message);
      }
    } finally {
      if (attempt === 1 || attempt >= retryCount) {
        setLoading(false);
      }
    }
  }, [endpoint, retryCount, retryDelay, options.requestOptions]);

  // Verificar si el cache está vigente
  const isCacheValid = useMemo(() => {
    if (!lastFetch || !data) return false;
    return (new Date() - lastFetch) < cacheTTL;
  }, [lastFetch, data, cacheTTL]);

  // Función para refetch manual
  const refetch = useCallback(() => {
    fetchWithRetry();
  }, [fetchWithRetry]);

  // Efecto principal para fetch de datos
  useEffect(() => {
    if (!endpoint) return;

    // Si el cache es válido, no hacer fetch
    if (isCacheValid) {
      setLoading(false);
      return;
    }

    fetchWithRetry();

    const abortInstance = abortControllerRef.current;

    // Cleanup
    return () => {
      if (abortInstance) {
        abortInstance.abort();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [endpoint, fetchWithRetry, isCacheValid, dependencies]); // Removed spread from dependencies

  // Revalidar cuando la ventana recibe foco
  useEffect(() => {
    if (!revalidateOnFocus) return;

    const handleFocus = () => {
      if (!isCacheValid) {
        refetch();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [revalidateOnFocus, isCacheValid, refetch]);

  return {
    data,
    loading,
    error,
    lastFetch,
    refetch,
    isCacheValid,
  };
};

// Hook para cuentas del usuario
export const useSecureAccounts = () => {
  const { data, loading, error, refetch } = useSecureData('/accounts', {
    cacheTTL: 2 * 60 * 1000, // Cache de 2 minutos para datos financieros
    retryCount: 5, // Más reintentos para datos críticos
  });

  // Calcular totales de forma segura
  const totals = useMemo(() => {
    if (!data || !Array.isArray(data)) return null;

    try {
      const total = data.reduce((sum, account) => {
        const balance = parseFloat(account.balance) || 0;
        return sum + balance;
      }, 0);

      const available = data.reduce((sum, account) => {
        const available = parseFloat(account.availableBalance) || 0;
        return sum + available;
      }, 0);

      return {
        total: total.toFixed(2),
        available: available.toFixed(2),
        accounts: data.length,
      };
    } catch (error) {
      console.error('Error calculando totales:', error);
      return null;
    }
  }, [data]);

  return {
    accounts: data,
    totals,
    loading,
    error,
    refetch,
  };
};

// Hook para transferencias seguras
export const useSecureTransfers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transferHistory, setTransferHistory] = useState([]);

  // Crear transferencia con validaciones adicionales
  const createTransfer = useCallback(async (transferData) => {
    setLoading(true);
    setError(null);

    try {
      // Validaciones del lado cliente
      const amount = parseFloat(transferData.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Monto inválido');
      }

      if (amount > 50000) { // Límite de seguridad
        throw new Error('Monto excede el límite máximo de transferencia');
      }

      if (!transferData.fromAccountId || !transferData.toAccountId) {
        throw new Error('Cuentas de origen y destino son requeridas');
      }

      if (transferData.fromAccountId === transferData.toAccountId) {
        throw new Error('La cuenta de origen no puede ser igual a la de destino');
      }

      // Sanitizar descripción (solo trim y límite de longitud, React escapa contra XSS al renderizar)
      const sanitizedDescription = transferData.description
        ?.trim()
        ?.substring(0, 100) || '';

      const result = await secureApi.createSecureTransfer({
        ...transferData,
        amount: amount.toFixed(2),
        description: sanitizedDescription,
      });

      // Agregar al historial local
      setTransferHistory(prev => [result, ...prev.slice(0, 9)]); // Mantener solo 10 más recientes

      return { success: true, data: result };

    } catch (error) {
      const errorMessage = error.message || 'Error en la transferencia';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    transferHistory,
    createTransfer,
    clearError: () => setError(null),
  };
};

// Hook para transacciones con paginación segura
export const useSecureTransactions = (accountId, pageSize = 20) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const loadTransactions = useCallback(async (resetPage = false) => {
    if (!accountId) return;

    const currentPage = resetPage ? 0 : page;
    setLoading(true);
    setError(null);

    try {
      const result = await secureApi.secureRequest(`/accounts/${accountId}/transactions`, {
        method: 'GET',
        headers: {
          'X-Page': currentPage.toString(),
          'X-Page-Size': pageSize.toString(),
        },
      });

      const newTransactions = result.transactions || [];

      if (resetPage) {
        setTransactions(newTransactions);
        setPage(0);
      } else {
        setTransactions(prev => [...prev, ...newTransactions]);
      }

      setHasMore(newTransactions.length === pageSize);
      setPage(currentPage + 1);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [accountId, page, pageSize]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadTransactions(false);
    }
  }, [loading, hasMore, loadTransactions]);

  const refresh = useCallback(() => {
    setTransactions([]);
    setPage(0);
    setHasMore(true);
    loadTransactions(true);
  }, [loadTransactions]);

  useEffect(() => {
    if (accountId) {
      refresh();
    }
  }, [accountId, refresh]);

  return {
    transactions,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
};

// Hook para notificaciones en tiempo real seguras
export const useSecureNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [unreadCount, setUnreadCount] = useState(0);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);

  // Conectar a WebSocket seguro
  const connectWebSocket = useCallback(() => {
    try {
      const token = secureApi.getSecureToken();
      if (!token) return;

      const wsUrl = `${process.env.REACT_APP_APISIX_WS_URL}/notifications?token=${encodeURIComponent(token)}`;

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket conectado de forma segura');
        setConnectionStatus('connected');

        // Heartbeat cada 30 segundos
        heartbeatRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Validar mensaje
          if (!message.type || !message.id) {
            console.warn('Mensaje WebSocket inválido recibido');
            return;
          }

          if (message.type === 'notification') {
            setNotifications(prev => [message, ...prev.slice(0, 49)]); // Máximo 50 notificaciones
            setUnreadCount(prev => prev + 1);
          }

        } catch (error) {
          console.error('Error procesando mensaje WebSocket:', error);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Error en WebSocket:', error);
        setConnectionStatus('error');
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket cerrado:', event.code, event.reason);
        setConnectionStatus('disconnected');

        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
        }

        // Reconectar automáticamente si no fue intencional
        if (event.code !== 1000) { // 1000 = cierre normal
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        }
      };

    } catch (error) {
      console.error('Error conectando WebSocket:', error);
      setConnectionStatus('error');
    }
  }, []);

  // Marcar notificación como leída
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await secureApi.secureRequest(`/notifications/${notificationId}/read`, {
        method: 'POST',
      });

      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));

    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  }, []);

  // Marcar todas como leídas
  const markAllAsRead = useCallback(async () => {
    try {
      await secureApi.secureRequest('/notifications/read-all', {
        method: 'POST',
      });

      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );

      setUnreadCount(0);

    } catch (error) {
      console.error('Error marcando todas las notificaciones como leídas:', error);
    }
  }, []);

  // Conectar al montar y limpiar al desmontar
  useEffect(() => {
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [connectWebSocket]);

  return {
    notifications,
    connectionStatus,
    unreadCount,
    markAsRead,
    markAllAsRead,
    reconnect: connectWebSocket,
  };
};

// Hook para validación de formularios seguros
export const useSecureForm = (initialValues, validationRules) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);

  // Sanitizar o dar formato a valor de entrada
  const sanitizeValue = useCallback((value, fieldName) => {
    if (typeof value !== 'string') return value;

    let sanitized = value;

    // Formateo específico por tipo de campo (UX)
    if (fieldName.includes('email')) {
      sanitized = sanitized.toLowerCase().trim();
    } else if (fieldName.includes('amount') || fieldName.includes('money')) {
      sanitized = sanitized.replace(/[^\d.,]/g, '');
    } else if (fieldName.includes('phone')) {
      sanitized = sanitized.replace(/[^\d+()-\s]/g, '');
    }

    return sanitized;
  }, []);

  // Validar campo individual
  const validateField = useCallback((fieldName, value) => {
    const rules = validationRules[fieldName];
    if (!rules) return null;

    for (const rule of rules) {
      const error = rule(value);
      if (error) return error;
    }

    return null;
  }, [validationRules]);

  // Cambiar valor de campo
  const setValue = useCallback((fieldName, value) => {
    const sanitizedValue = sanitizeValue(value, fieldName);

    setValues(prev => ({
      ...prev,
      [fieldName]: sanitizedValue,
    }));

    // Validar si el campo ya fue tocado
    if (touched[fieldName]) {
      const error = validateField(fieldName, sanitizedValue);
      setErrors(prev => ({
        ...prev,
        [fieldName]: error,
      }));
    }
  }, [sanitizeValue, validateField, touched]);

  // Marcar campo como tocado
  const setFieldTouched = useCallback((fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true,
    }));

    // Validar el campo
    const error = validateField(fieldName, values[fieldName]);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error,
    }));
  }, [validateField, values]);

  // Validar todo el formulario
  const validateAll = useCallback(() => {
    const newErrors = {};

    Object.keys(validationRules).forEach(fieldName => {
      const error = validateField(fieldName, values[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    setErrors(newErrors);
    setTouched(
      Object.keys(validationRules).reduce((acc, fieldName) => {
        acc[fieldName] = true;
        return acc;
      }, {})
    );

    return Object.keys(newErrors).length === 0;
  }, [validationRules, validateField, values]);

  // Resetear formulario
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  // Verificar si el formulario es válido
  useEffect(() => {
    const hasErrors = Object.values(errors).some(error => error);
    const allTouched = Object.keys(validationRules).every(fieldName => touched[fieldName]);
    setIsValid(!hasErrors && allTouched);
  }, [errors, touched, validationRules]);

  return {
    values,
    errors,
    touched,
    isValid,
    setValue,
    setFieldTouched,
    validateAll,
    reset,
  };
};

const secureApiHooks = {
  useSecureAuth,
  useSecureData,
  useSecureAccounts,
  useSecureTransfers,
  useSecureTransactions,
  useSecureNotifications,
  useSecureForm,
};

export default secureApiHooks;